import "../styles/globals.css";
import "@fortawesome/fontawesome-svg-core/styles.css";
import "../styles/algolia-overrides.scss";

import type { AppProps } from "next/app";
import "@dreambigger/design-system/src/styles/index.scss";
import { DefaultThemeProvider, Spin } from "@dreambigger/design-system";
import InspireLayout from "@dreambigger/shared/src/components/layout/inspire-layout";
import {
  api,
  tokenHelper,
} from "@dreambigger/shared/src/api/weserve/api-helper";
import { AuthProvider } from "@dreambigger/shared/src/components/auth";
import { Community, Event } from "@dreambigger/shared/src/types";
import { useEffect, useMemo } from "react";
import { FooterCta } from "@dreambigger/communities/src/components";
import "nprogress/nprogress.css";
import NProgress from "nprogress";
import { Router } from "next/router";
import useMixpanel from "@dreambigger/shared/src/hooks/use-mixpanel";
import { ShareStoryFooter } from "@dreambigger/inspire-events/src/components/ui";

NProgress.configure({ showSpinner: false });
Router.events.on("routeChangeStart", () => NProgress.start());
Router.events.on("routeChangeComplete", () => NProgress.done());
Router.events.on("routeChangeError", () => NProgress.done());

// A list of pathnames that we don't want to be wrapped with inspire wrappers, such as 404 pages.
const skipWrappersArr = ["/404"];

function MyApp({ Component, pageProps, router }: AppProps) {
  const isFixed =
    (pageProps?.story?.images && pageProps?.story?.images.length > 0) ||
    undefined;

  const community = pageProps.community as Community;
  const event = pageProps.event as Event | undefined;
  const communityColors = community?.meta?.brand?.colors;

  const mixpanel = useMixpanel();

  useEffect(() => {
    // Initialize mixpanel
    mixpanel.init();

    // Register the Company Name and orgId mixpanel properties that should be apart of each mixpanel call.
    if (community) {
      mixpanel.register({
        "Organization Name": community.organization.name,
        "Organization Id": community.organization.id,
        // Legacy variables
        "Company Name": community.organization.name,
        orgId: community.organization.id,
      });
    }
  }, [community]);

  // FOOTER
  // * All pages have a footer, but on some pages and situations within pages, there is also a prefooter.
  //   Example: On event and fundraiser detail pages, there is a prefooter if no stories have been shared yet - <ShareStoryFooter>.
  // * Prefooters can be added to pages by specifying their urls in the switch below.
  const preFooterContent = useMemo(() => {
    // First, check if there is metadata for a normal footer.
    const communityFooter = community?.meta?.footerBanner ? (
      <FooterCta
        ctaText={community.meta.footerBanner.title}
        linkText={community.meta.footerBanner.ctaTitle}
        image={community.meta.footerBanner.image}
        ctaUrl={community.meta.footerBanner.ctaAction}
        background={community.meta.footerBanner.backgroundColor}
      />
    ) : null;

    // If there is, check if a prefooter should be added to it.
    switch (true) {
      case router.pathname.startsWith("/community/[communitySlug]/"):
        if (!community?.meta?.footerBanner) return null;

        return communityFooter;
      case router.pathname.startsWith("/events/[eventId]/detail"):
        if (event && event.storyCount === 0) {
          return <ShareStoryFooter event={event} />;
        }
        if (!community?.meta?.footerBanner) return null;

        return communityFooter;
      default:
        return null;
    }
  }, [community, event]);

  // Enrich orgBootstrap with org meta gtmContainerId
  const orgBootstrap = {
    ...pageProps.orgBootstrap,
    gtmContainerId: community?.organization?.meta?.gtmContainerId,
  };

  if (skipWrappersArr.includes(router.pathname)) {
    // We don't need wrappers for these pages so just return the page.
    return <Component {...pageProps} />;
  }

  if (!pageProps.orgBootstrap || !pageProps.community) {
    return <Spin style={{ width: "100%", marginTop: 150 }} />;
  }

  return (
    <AuthProvider api={api} tokenHelper={tokenHelper}>
      <DefaultThemeProvider colors={communityColors}>
        <InspireLayout
          orgBootstrap={orgBootstrap}
          fixed={isFixed}
          preFooterContent={preFooterContent}
        >
          <Component {...pageProps} />
        </InspireLayout>
      </DefaultThemeProvider>
    </AuthProvider>
  );
}
export default MyApp;
